import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { useBlocker } from "react-router-dom";
import { Button, Dialog } from "@/components/ui";

/**
 * Warnt vor dem Verlassen einer Eingabeseite mit ungespeicherten Änderungen (Zurück-Button des Browsers,
 * Menü, Links, Seite schließen/neu laden) und bietet "Speichern" / "Verwerfen" / "Weiter bearbeiten" an.
 *
 * Erkennung ohne Pflege je Feld: Beim ersten Anklicken/Tippen auf der Seite wird der damalige Zustand als
 * Ausgangsstand gemerkt (`getSnapshot`); "geändert" heißt, der aktuelle Zustand weicht davon ab.
 * Nach erfolgreichem Speichern `markSaved()` aufrufen, vor einer gewollten Navigation (nach Löschen,
 * Umwandeln, …) `allowLeave()` — dann wird nicht nachgefragt.
 */
export function useUnsavedGuard({
  ready,
  getSnapshot,
}: {
  ready: boolean;
  getSnapshot: () => string;
}) {
  const baseline = useRef<string | null>(null);
  const bypass = useRef(false);
  const pendingProceed = useRef(false);
  const saveFn = useRef<(() => boolean) | null>(null);

  const snapshot = ready ? getSnapshot() : null;
  const dirty =
    ready && baseline.current !== null && snapshot !== baseline.current;
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !bypass.current &&
      dirtyRef.current &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  // Tab schließen / neu laden
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const capture = () => {
    if (ready && baseline.current === null) baseline.current = getSnapshot();
  };

  const markSaved = useCallback(() => {
    baseline.current = null;
    if (pendingProceed.current) {
      pendingProceed.current = false;
      if (blocker.state === "blocked") blocker.proceed();
    }
  }, [blocker]);

  const allowLeave = useCallback(() => {
    bypass.current = true;
    pendingProceed.current = false;
    if (blocker.state === "blocked") blocker.reset();
    setTimeout(() => (bypass.current = false), 1500);
  }, [blocker]);

  const dialog: ReactNode = (
    <Dialog
      open={blocker.state === "blocked"}
      onOpenChange={(v) => {
        if (!v && blocker.state === "blocked") {
          pendingProceed.current = false;
          blocker.reset();
        }
      }}
      guardClose={false}
      title="Nicht gespeicherte Änderungen"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              pendingProceed.current = false;
              blocker.reset?.();
            }}
          >
            Weiter bearbeiten
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              baseline.current = null;
              blocker.proceed?.();
            }}
          >
            Verwerfen
          </Button>
          {saveFn.current && (
            <Button
              variant="primary"
              onClick={() => {
                pendingProceed.current = true;
                const started = saveFn.current?.();
                if (started === false) pendingProceed.current = false;
              }}
            >
              Speichern
            </Button>
          )}
        </>
      }
    >
      <p className="text-sm text-text-secondary">
        Du hast Eingaben gemacht, die noch nicht gespeichert sind. Willst du
        wirklich abbrechen oder vorher speichern?
      </p>
    </Dialog>
  );

  return {
    dirty,
    /** auf das Wurzel-Element der Seite spreaden: merkt sich den Ausgangsstand bei der ersten Eingabe */
    rootProps: { onPointerDownCapture: capture, onKeyDownCapture: capture },
    /** Speichern-Funktion der Seite registrieren; gibt zurück, ob das Speichern gestartet wurde */
    setSave: (fn: () => boolean) => {
      saveFn.current = fn;
    },
    markSaved,
    allowLeave,
    dialog,
  };
}
